import { Box, Stack, Typography, Checkbox, Button, Link } from '@dtx-company/design-system/src'
import { Coupon } from '@dtx-company/true-common/src/types/Coupon'
import { PPESummaryTopSection } from './PPESummaryTopSection'
import {
  Plan,
  PlanExtraDetail,
  PlanExtraRender,
  PlanPurchaseExperienceMode
} from '@dtx-company/true-common/src/types/Plan'
import {
  PlanCostSummary,
  PlanCostSummaryType
} from '@dtx-company/inter-app/src/types/PlanCostSummary'
import { PlanExtrasForm } from '../../../ProFlex/AddOns'
import { useMemo, useState } from 'react'
import { formatDate } from '@dtx-company/app/shared/code/src/utils/my-flowcodes'
import { getPlanTypeDirection } from '../../../../utils/getPlanTypeDirection'
import { getPriceText } from '../../../../utils/getPriceText'
import { isPilotPlanType } from '@dtx-company/app/shared/common/src/utils/isPilotPlanType'

export interface PPESummarySectionProps {
  selectedPlan: Plan
  currentPlan?: Plan
  promo: Partial<Coupon>
  planCostSummary: PlanCostSummary | undefined
  currentPlanExtraDetails?: PlanExtraDetail[]
  planExtraFormValues: PlanExtrasForm
  planExtraRender?: PlanExtraRender[]
  isProcessing: boolean
  isFormValid: boolean
  showChangeAddons: boolean
  mode?: PlanPurchaseExperienceMode
  hasDowngradeError?: boolean
}

export function PPESummarySection(props: PPESummarySectionProps): JSX.Element {
  const isCurrentPlanSelected = props.selectedPlan.id === props.currentPlan?.id
  const isPilot = props.currentPlan ? isPilotPlanType(props.currentPlan.type) : false
  const isDowngrade =
    !isPilot && props.currentPlan?.type && props.selectedPlan.type
      ? getPlanTypeDirection(props.currentPlan?.type, props.selectedPlan.type) < 0
      : false
  const [isConfirmed, setIsConfirmed] = useState(false)
  const isSubmitDisabled =
    (isCurrentPlanSelected &&
      props.mode !== PlanPurchaseExperienceMode.CHANGE_PAYMENT &&
      !props.showChangeAddons) ||
    !props.isFormValid ||
    (isDowngrade ? !isConfirmed : false)

  const grandTotalPriceText = (() => {
    if (props.planCostSummary) {
      if (props.planCostSummary.grandTotalInCents !== undefined) {
        return getPriceText({ priceInCents: props.planCostSummary.grandTotalInCents })
      } else {
        // Shouldn't happen but technically possible
        return ''
      }
    }
    return 'Calculating...'
  })()

  const isCredit = (props.planCostSummary?.grandTotalInCents || 0) < 0
  const submitButtonLabel = useMemo(() => {
    if (props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT) {
      return 'Update payment'
    }
    if (isCurrentPlanSelected && !props.showChangeAddons) {
      return 'Already on this plan'
    }
    return 'Complete checkout'
  }, [props.mode, props.showChangeAddons, isCurrentPlanSelected])

  const dueDateText = useMemo(() => {
    if (isCredit) return ''
    if (props.planCostSummary?.downgradePeriodEndDate) {
      return `due ${formatDate(props.planCostSummary.downgradePeriodEndDate)}`
    }
    if (props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT) {
      return 'due on your next billing date'
    }
    return 'due today'
  }, [props.mode, isCredit, props.planCostSummary?.downgradePeriodEndDate])

  const grandTotalLabelText = useMemo(() => {
    if (props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT) return 'New Total'
    if (
      props.mode === PlanPurchaseExperienceMode.PURCHASE_ADDONS &&
      (!props.planCostSummary?.type || props.planCostSummary.type === PlanCostSummaryType.SAME)
    ) {
      return ''
    }
    return 'Total'
  }, [props.mode, props.planCostSummary?.type])

  const grandTotalLineText = useMemo(() => {
    if (!grandTotalLabelText) return ''
    if (dueDateText) {
      return `${grandTotalLabelText} (${dueDateText})`
    }
    return grandTotalLabelText
  }, [grandTotalLabelText, dueDateText])

  if (props.hasDowngradeError) return <_PlanDowngradeErrorAlert />

  return (
    <>
      <PPESummaryTopSection
        selectedPlan={props.selectedPlan}
        mode={props.mode}
        promo={props.promo}
        planCostSummary={props.planCostSummary}
        currentPlanExtraDetails={props.currentPlanExtraDetails}
        planExtraFormValues={props.planExtraFormValues}
        planExtraRender={props.planExtraRender}
      />
      <Box
        sx={{
          py: 4
        }}
      >
        <Box>
          {grandTotalLineText && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="bodyMedium" fontWeight="bold">
                  {grandTotalLineText}
                </Typography>
                <Typography
                  component="p"
                  variant="subtitleMedium"
                  fontWeight="bold"
                  color={isCredit ? 'tertiary.main' : 'primary.main'}
                >
                  {grandTotalPriceText}
                </Typography>
              </Stack>
              <Typography
                component="p"
                variant="bodyMedium"
                color="text.secondary"
                textAlign="right"
              >
                plus applicable sales tax
              </Typography>
            </>
          )}
          {isCredit && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="labelSmall">
                This will show up as a credit on your account and be applied to your next billing
                cycle.
              </Typography>
            </Box>
          )}
          {isDowngrade && (
            <Box sx={{ mt: 4 }}>
              <Checkbox
                label="I understand that my subscription will change and that from now on my most recent
                  codes and pages that fit within my subscription limit will be saved."
                checked={isConfirmed}
                onChange={(_event, checked) => setIsConfirmed(checked)}
              />
            </Box>
          )}
          <Box sx={{ mt: 6, mb: 1 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isSubmitDisabled}
              loading={props.isProcessing}
            >
              {props.isProcessing ? 'Processing...' : submitButtonLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  )
}

function _PlanDowngradeErrorAlert(): JSX.Element {
  return (
    <Box
      role="alert"
      aria-live="polite"
      sx={{
        display: 'block',
        backgroundColor: 'background.paper',
        borderTop: '1px solid',
        borderTopColor: 'border.outline',
        py: 4,
        overflow: 'hidden',
        transition: 'border-color 100ms linear',
        position: 'sticky',
        zIndex: 1,
        bottom: 0,
        '& > *': {
          transition: 'transform 100ms linear',
          transform: 'scale(1.01)'
        }
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          a: {
            color: 'primary.main'
          }
        }}
      >
        <Typography variant="labelLarge" color="error">
          Downgrading through our website is not supported at this time. Please contact{' '}
        </Typography>
        <Link href="mailto:support@flowcode.com">support@flowcode.com</Link>{' '}
        <Typography variant="labelLarge" color="error">
          to downgrade to a lower plan.
        </Typography>
      </Box>
    </Box>
  )
}
